import { routePaths } from 'components/Routes/data';
import { BulkUploadTypes, UploadTypeConfig, UploadTypeConfigCallback } from 'constants/bulkUpload';
import { format } from 'date-fns';
import { getRunPayrollMonthList } from 'utils/Dates';

const getColumns = (): NonNullable<UploadTypeConfig['preview']['columns']> => [
  {
    title: 'Name',
    render: (item) => item.file_row?.commonProperties?.employeeName,
  },
  {
    title: 'Email Address',
    render: (item) => item.file_row?.commonProperties?.email,
  },
  {
    title: 'Payroll Month',
    render: (item) => {
      const pm = item.file_row?.payrollMonth;
      if (!pm) return null;
      try {
        return format(new Date(pm as string), 'MMMM yyyy');
      } catch {
        return pm;
      }
    },
  },
  {
    title: 'Gross Salary (₹)',
    render: (item) => item.file_row?.grossSalaryRupees,
  },
  {
    title: 'TDS Amount (₹)',
    render: (item) => item.file_row?.tdsAmountRupees,
  },
];

const getCurrentMonthStr = () => {
  const today = new Date();
  today.setDate(1);
  today.setHours(0, 0, 0, 0);
  return format(today, 'yyyy-MM-dd');
};

// Returns options from selectedMonth-4 to selectedMonth+8 (13 options total).
// getRunPayrollMonthList generates -4 to +7 relative to the given month (12 entries).
const getPayrollMonthOptions = (selectedMonth: string) => {
  const monthList = getRunPayrollMonthList(selectedMonth);
  return monthList.map((month) => ({
    value: format(month, 'yyyy-MM-dd'),
    label: format(month, 'MMMM yyyy'),
  }));
};

export const bulkTdsOverrideConfig: UploadTypeConfigCallback = (data) => {
  const urlParams = new URLSearchParams(data.history.location.search);
  const payrollMonth = urlParams.get('payrollMonth') || '';

  return {
    initiation: {
      pageTitle: 'Bulk TDS Override',
      step1Title: '1. Download the TDS Override template',
      step1Subtitle:
        'Download the template and fill in the employee email and TDS amount in rupees, then upload it here.',
      instructions: [
        'Enter the TDS amount in rupees (whole numbers only).',
        'Only one override per employee per payroll month is allowed.',
        'Do not add or remove any columns.',
        'There is a limit of 500 employees per upload.',
        'Mandatory fields are marked with a * sign.',
      ],
      hasMonthSelector: true,
      skipMonthChangeConfirmation: true,
      dropdownSelectorLabel: 'Select payroll month',
      getDropdownOptions: getPayrollMonthOptions,
      defaultDropdownValue: getCurrentMonthStr(),
      onDropdownOptionChange(selectedMonth) {
        if (selectedMonth) {
          data.history.push({
            pathname: routePaths.bulkUploads.root,
            search: `?type=${BulkUploadTypes.TYPE_BULK_TDS_OVERRIDE}&payrollMonth=${selectedMonth}`,
          });
        }
      },
      onSampleTemplateDownload() {
        window.open(
          `/v2/api/bulk-tds-override/download-template${payrollMonth ? `?payroll_month=${payrollMonth}` : ''}`,
          '_blank',
        );
      },
    },
    preview: {
      columns: getColumns(),
      renderConfirmationText: () =>
        'Almost done! Please confirm to proceed with the bulk TDS override.',
      successModalCtaText: 'Go to Bulk Actions',
      successModalMainText: 'TDS overrides have been applied successfully.',
      successModalSubText: () =>
        'The updated TDS amounts will be reflected in the next payroll computation.',
      onSuccessModalCtaClick() {
        data.history.push(routePaths.bulkActions);
      },
      onDownloadErrorReport(history) {
        history?.push({
          pathname: routePaths.bulkUploads.initiate,
          search: `type=${BulkUploadTypes.TYPE_BULK_TDS_OVERRIDE}`,
        });
      },
    },
  };
};
